import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface ConfidenceIndicatorProps {
  confidence: number; // 0-1
  showLabel?: boolean;
  variant?: "progress" | "badge";
  className?: string;
}

function getConfidenceLevel(confidence: number): {
  label: string;
  color: string;
  bgColor: string;
} {
  if (confidence >= 0.8) {
    return {
      label: "High",
      color: "text-emerald-700 dark:text-emerald-400",
      bgColor: "bg-emerald-500",
    };
  }
  if (confidence >= 0.5) {
    return {
      label: "Medium",
      color: "text-amber-700 dark:text-amber-400",
      bgColor: "bg-amber-500",
    };
  }
  return {
    label: "Low",
    color: "text-red-700 dark:text-red-400",
    bgColor: "bg-red-500",
  };
}

export function ConfidenceIndicator({
  confidence,
  showLabel = true,
  variant = "progress",
  className,
}: ConfidenceIndicatorProps) {
  const percentage = Math.round(confidence * 100);
  const { label, color, bgColor } = getConfidenceLevel(confidence);

  if (variant === "badge") {
    return (
      <Badge
        variant="outline"
        className={cn(
          "gap-1.5 font-normal",
          color,
          className
        )}
      >
        <span
          className={cn("size-2 rounded-full", bgColor)}
          aria-hidden="true"
        />
        {percentage}%
        {showLabel && <span className="text-muted-foreground">({label})</span>}
      </Badge>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Progress
        value={percentage}
        className={cn(
          "h-2 w-16",
          confidence >= 0.8 && "[&>[data-slot=progress-indicator]]:bg-emerald-500",
          confidence >= 0.5 &&
            confidence < 0.8 &&
            "[&>[data-slot=progress-indicator]]:bg-amber-500",
          confidence < 0.5 && "[&>[data-slot=progress-indicator]]:bg-red-500"
        )}
        aria-label={`Confidence: ${percentage}%`}
      />
      {showLabel && (
        <span className={cn("text-xs", color)}>
          {percentage}%
        </span>
      )}
    </div>
  );
}

/*
 * Usage Example:
 *
 * import { ConfidenceIndicator } from "@/components/confidence-indicator";
 *
 * function TableCard({ table }) {
 *   return (
 *     <div>
 *       <span>{table.name}</span>
 *       {table.confidence && (
 *         <ConfidenceIndicator confidence={table.confidence} />
 *       )}
 *     </div>
 *   );
 * }
 */
